import { useEffect, useRef, useState } from 'react';
import type { Participant } from '@sfc/sdk';
import { platform } from '../api/client';
import { forgetSignInToken } from './route';

/**
 * The landing an emailed link opens.
 *
 * Spending the link is a write, and React in development mounts an effect
 * twice, so the guard below is not defensive dressing: without it the second
 * mount spends a link the first one just used and the screen reports a failure
 * for a sign-in that worked.
 */
export function ConsumeLink({
  token,
  onSignedIn,
  onFailed,
}: {
  token: string;
  onSignedIn: (participant: Participant) => void;
  onFailed: () => void;
}): React.JSX.Element {
  const [failed, setFailed] = useState(false);
  const spent = useRef(false);

  useEffect(() => {
    if (spent.current) return;
    spent.current = true;

    void (async () => {
      try {
        const participant = await platform.match.consumeSignInLink(token);
        // Out of the address bar before anything else, so a one-time
        // credential is not left in the history of a shared device.
        forgetSignInToken();
        onSignedIn(participant);
      } catch {
        /*
         * The dead token stays in the address bar until the reader moves on.
         *
         * Clearing it here unmounts this screen before it has drawn: the path
         * is what decides whether the app is on the link landing at all, so
         * removing it sends somebody whose link had expired back to the
         * sign-in form with no explanation of why. It is cleared on the way
         * out instead, and leaving it is harmless because it no longer opens
         * anything.
         */
        setFailed(true);
      }
    })();
  }, [token, onSignedIn]);

  if (!failed) {
    return (
      <main className="screen">
        <p className="loading" data-testid="signing-in">
          Signing you in…
        </p>
      </main>
    );
  }

  return (
    <main className="screen">
      <div className="pane">
        <header className="pane__head">
          <span className="mark" aria-hidden="true">
            SFC
          </span>
          <h1>That link cannot be used</h1>
          {/* The API will not say which of expired, spent or forged it was, so
              neither does this. All three have the same answer anyway. */}
          <p className="lede">
            Links work once and last twenty minutes. Ask for a fresh one and it will arrive in a
            moment.
          </p>
        </header>

        <button className="button button--primary" type="button" onClick={onFailed} data-testid="ask-again">
          Email me a new link
        </button>
      </div>
    </main>
  );
}
